import React from 'react';
import { NavigationContainer, Route } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { PaperProvider } from 'react-native-paper';
import { appPaperTheme } from './app/theme/paperTheme';
import LandingScreen from './app/screens/LandingScreen';
import LoginScreen from './app/screens/LoginScreen';
import RegisterScreen from './app/screens/RegisterScreen';
import HomeScreen from './app/screens/HomeScreen';
import HelpFeedScreen from './app/screens/HelpFeedScreen';
import ViewResponsesScreen from './app/screens/ViewResponsesScreen';
import HelpResponseScreen from './app/screens/HelpResponseScreen';
import AllHelpTicketsScreen from './app/screens/AllHelpTicketsScreen';
import { RootStackParamList } from './app/types/navigation';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import FeedbackScreen from './app/screens/FeedbackScreen';
import ProfileSettingsScreen from './app/screens/ProfileSettingsScreen';
import ResourcesScreen from './app/screens/ResourcesScreen';
import AddResourceScreen from './app/screens/AddResourceScreen';

const Stack = createNativeStackNavigator<RootStackParamList>();

const BRAND = 'Teachers Lobby';
const LANDING_DOCUMENT_TITLE = 'Teachers Lobby | Simplifying Teacher Workflows';

const SCREEN_TITLES: Record<keyof RootStackParamList, string> = {
  Landing: 'Landing',
  Login: 'Login',
  Register: 'Register',
  Home: 'Home',
  HelpFeed: 'Help Feed',
  AllHelpTickets: 'All Help Tickets',
  HelpResponse: 'Help Response',
  ViewResponses: 'View Responses',
  Feedback: 'Feedback',
  ProfileSettings: 'Profile',
  Resources: 'Resources',
  AddResource: 'Add Resource',
  MyResponses: 'My Responses',
};

const documentTitle = {
  enabled: true,
  formatter: (options: Record<string, unknown> | undefined, route: Route<string> | undefined) => {
    const routeName = route?.name ?? 'Landing';
    if (routeName === 'Landing') {
      return LANDING_DOCUMENT_TITLE;
    }
    const pageTitle =
      (options?.title as string | undefined) ??
      SCREEN_TITLES[routeName as keyof RootStackParamList] ??
      routeName;
    return `${pageTitle} | ${BRAND}`;
  },
};

export default function App() {
  const { width } = useWindowDimensions();
  return (
    <PaperProvider theme={appPaperTheme}>
      <View style={[styles.appContainer, width >= 900 && styles.desktopContainer]}>
        <NavigationContainer documentTitle={documentTitle}>
          <Stack.Navigator
            initialRouteName="Landing"
            screenOptions={({ route }) => ({
              headerShown: false,
              title: SCREEN_TITLES[route.name as keyof RootStackParamList],
            })}
          >
            <Stack.Screen name="Landing" component={LandingScreen} />
            <Stack.Screen name="Login" component={LoginScreen} />
            <Stack.Screen name="Register" component={RegisterScreen} />
            <Stack.Screen name="Home" component={HomeScreen} />
            <Stack.Screen name="HelpFeed" component={HelpFeedScreen} />
            <Stack.Screen name="AllHelpTickets" component={AllHelpTicketsScreen} />
            <Stack.Screen name="HelpResponse" component={HelpResponseScreen} />
            <Stack.Screen name="ViewResponses" component={ViewResponsesScreen} />
            <Stack.Screen name="Feedback" component={FeedbackScreen} />
            <Stack.Screen name="ProfileSettings" component={ProfileSettingsScreen} />
            <Stack.Screen name="Resources" component={ResourcesScreen} />
            <Stack.Screen name="AddResource" component={AddResourceScreen} />
          </Stack.Navigator>
        </NavigationContainer>
      </View>
    </PaperProvider>
  );
}

const styles = StyleSheet.create({
  appContainer: {
    flex: 1,
    backgroundColor: '#ffffff',
    width: '100%',
    minHeight: '100%',
  },
  desktopContainer: {
    marginHorizontal: 'auto',
    alignSelf: 'center',
    width: '100%',
  },
});
